import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Image as ImageIcon,
  Video as VideoIcon,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  listGallery,
  createGalleryItem,
  updateGalleryItem,
  deleteGalleryItem,
} from "@/lib/api";

const LAYOUTS = [
  { value: "hero", label: "Hero — Large featured (8 cols, landscape)" },
  { value: "tall", label: "Tall — Portrait (4 cols)" },
  { value: "half", label: "Half — Half width (6 cols)" },
  { value: "wide", label: "Wide — Full banner (12 cols)" },
];

const EMPTY = {
  media_type: "image",
  url: "",
  caption: "",
  layout: "half",
  sort_order: 0,
  active: true,
};

export default function GalleryManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await listGallery(true);
      setItems(r);
    } catch {
      toast.error("Could not load gallery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onAdd = async (e) => {
    e.preventDefault();
    if (!form.url.trim()) {
      toast.error("Paste an image or video URL first.");
      return;
    }
    setSaving(true);
    try {
      const nextOrder = items.length
        ? Math.max(...items.map((i) => i.sort_order || 0)) + 1
        : 1;
      const created = await createGalleryItem({
        ...form,
        sort_order: nextOrder,
      });
      setItems((prev) => [...prev, created]);
      setForm(EMPTY);
      toast.success("Added to gallery");
    } catch (err) {
      toast.error("Could not add item");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      const updated = await updateGalleryItem(item.id, { active: !item.active });
      setItems((prev) => prev.map((i) => (i.id === item.id ? updated : i)));
    } catch {
      toast.error("Update failed");
    }
  };

  const changeLayout = async (item, layout) => {
    try {
      const updated = await updateGalleryItem(item.id, { layout });
      setItems((prev) => prev.map((i) => (i.id === item.id ? updated : i)));
    } catch {
      toast.error("Update failed");
    }
  };

  const editCaption = async (item, caption) => {
    if (caption === item.caption) return;
    try {
      const updated = await updateGalleryItem(item.id, { caption });
      setItems((prev) => prev.map((i) => (i.id === item.id ? updated : i)));
    } catch {
      toast.error("Update failed");
    }
  };

  const move = async (item, dir) => {
    const sorted = [...items].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex((i) => i.id === item.id);
    const swap = sorted[idx + dir];
    if (!swap) return;
    try {
      const [a, b] = await Promise.all([
        updateGalleryItem(item.id, { sort_order: swap.sort_order }),
        updateGalleryItem(swap.id, { sort_order: item.sort_order }),
      ]);
      setItems((prev) =>
        prev.map((i) => (i.id === a.id ? a : i.id === b.id ? b : i))
      );
    } catch {
      toast.error("Reorder failed");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete this ${item.media_type}?`)) return;
    try {
      await deleteGalleryItem(item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const sorted = [...items].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div data-testid="gallery-manager" className="space-y-10">
      {/* Add form */}
      <form
        onSubmit={onAdd}
        data-testid="gallery-add-form"
        className="bg-[#0D1322] border border-white/10 p-6 md:p-8"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Add to gallery</h2>
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40">
            Paste a public image or video URL
          </span>
        </div>

        <div className="grid md:grid-cols-12 gap-5">
          <Field label="Media type" cols="md:col-span-3">
            <Select value={form.media_type} onValueChange={(v) => update("media_type", v)}>
              <SelectTrigger
                data-testid="gm-type"
                className="bg-[#05080F] border-white/10 text-white rounded-none h-11"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0D1322] border-white/10 text-white rounded-none">
                <SelectItem value="image" data-testid="gm-type-image">Image</SelectItem>
                <SelectItem value="video" data-testid="gm-type-video">Video (MP4 / YouTube)</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field label="Layout" cols="md:col-span-4">
            <Select value={form.layout} onValueChange={(v) => update("layout", v)}>
              <SelectTrigger
                data-testid="gm-layout"
                className="bg-[#05080F] border-white/10 text-white rounded-none h-11"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0D1322] border-white/10 text-white rounded-none">
                {LAYOUTS.map((l) => (
                  <SelectItem key={l.value} value={l.value} data-testid={`gm-layout-${l.value}`}>
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Caption (optional)" cols="md:col-span-5">
            <Input
              data-testid="gm-caption"
              value={form.caption}
              onChange={(e) => update("caption", e.target.value)}
              placeholder="e.g. Lakehouse · 07:12"
              className="bg-[#05080F] border-white/10 text-white rounded-none h-11"
            />
          </Field>

          <Field label="URL" cols="md:col-span-12">
            <Input
              data-testid="gm-url"
              value={form.url}
              onChange={(e) => update("url", e.target.value)}
              placeholder="https://…   (Imgur / Google Drive direct / Dropbox / YouTube / .mp4)"
              className="bg-[#05080F] border-white/10 text-white rounded-none h-11 font-mono text-xs"
            />
          </Field>
        </div>

        <div className="flex items-center justify-between mt-6">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 max-w-md leading-relaxed">
            Free hosting tips: Imgur (image link), Google Drive (Share → &quot;Anyone with the link&quot;, use the direct image URL), or a YouTube link for videos.
          </p>
          <button
            type="submit"
            disabled={saving}
            data-testid="gm-add"
            className="inline-flex items-center gap-2 bg-[#E5A93B] hover:bg-[#FACC15] text-black px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Add Item
          </button>
        </div>
      </form>

      {/* List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">
            Items <span className="text-[#E5A93B]">/{items.length}</span>
          </h2>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-white/60 py-12 justify-center">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : sorted.length === 0 ? (
          <div className="border border-dashed border-white/10 bg-[#0D1322]/50 py-16 text-center">
            <span className="overline text-white/40">Empty gallery</span>
            <p className="text-white/50 text-sm mt-3">Add your first item above.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {sorted.map((it, idx) => (
              <GalleryCard
                key={it.id}
                item={it}
                index={idx}
                total={sorted.length}
                onToggle={() => toggleActive(it)}
                onDelete={() => remove(it)}
                onLayout={(v) => changeLayout(it, v)}
                onCaption={(v) => editCaption(it, v)}
                onMoveUp={() => move(it, -1)}
                onMoveDown={() => move(it, 1)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const GalleryCard = ({ item, index, total, onToggle, onDelete, onLayout, onCaption, onMoveUp, onMoveDown }) => {
  const [caption, setCaption] = useState(item.caption || "");
  useEffect(() => setCaption(item.caption || ""), [item.caption]);

  return (
    <div
      data-testid={`gallery-card-${item.id}`}
      className={`border bg-[#0D1322] ${item.active ? "border-white/10" : "border-white/5 opacity-60"}`}
    >
      <div className="aspect-[4/3] bg-black relative overflow-hidden">
        {item.media_type === "video" ? (
          <div className="w-full h-full flex items-center justify-center bg-black/50">
            <VideoIcon size={32} className="text-[#E5A93B]" />
            <span className="absolute bottom-2 left-2 font-mono text-[10px] tracking-wider uppercase text-white/60">
              VIDEO
            </span>
          </div>
        ) : (
          <img src={item.url} alt={item.caption || ""} className="w-full h-full object-cover" loading="lazy" />
        )}
        <div className="absolute top-2 right-2 flex items-center gap-1">
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            data-testid={`gc-open-${item.id}`}
            className="bg-black/60 backdrop-blur p-1.5 text-white/70 hover:text-[#E5A93B]"
            title="Open URL"
          >
            <ExternalLink size={12} />
          </a>
        </div>
        <div className="absolute top-2 left-2 font-mono text-[10px] tracking-wider uppercase bg-black/60 backdrop-blur px-2 py-1 text-white/70 flex items-center gap-1">
          {item.media_type === "image" ? <ImageIcon size={10} /> : <VideoIcon size={10} />}
          {item.layout}
        </div>
      </div>

      <div className="p-4 space-y-3">
        <Input
          data-testid={`gc-caption-${item.id}`}
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          onBlur={() => onCaption(caption)}
          placeholder="Caption"
          className="bg-[#05080F] border-white/10 text-white rounded-none h-9 text-sm"
        />
        <Select value={item.layout} onValueChange={onLayout}>
          <SelectTrigger
            data-testid={`gc-layout-${item.id}`}
            className="bg-[#05080F] border-white/10 text-white rounded-none h-9 text-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0D1322] border-white/10 text-white rounded-none">
            {LAYOUTS.map((l) => (
              <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center justify-between pt-2 border-t border-white/10">
          <div className="flex items-center gap-1">
            <IconBtn
              testid={`gc-up-${item.id}`}
              disabled={index === 0}
              onClick={onMoveUp}
              label="↑"
              title="Move up"
            />
            <IconBtn
              testid={`gc-down-${item.id}`}
              disabled={index === total - 1}
              onClick={onMoveDown}
              label="↓"
              title="Move down"
            />
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid={`gc-toggle-${item.id}`}
              onClick={onToggle}
              className="p-2 text-white/60 hover:text-[#E5A93B] transition"
              title={item.active ? "Hide from public site" : "Show on public site"}
            >
              {item.active ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
            <button
              data-testid={`gc-delete-${item.id}`}
              onClick={onDelete}
              className="p-2 text-white/60 hover:text-red-400 transition"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const IconBtn = ({ onClick, disabled, label, title, testid }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    title={title}
    className="font-mono text-xs px-2 py-1 border border-white/10 text-white/70 hover:bg-white/5 hover:text-[#E5A93B] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-white/70"
  >
    {label}
  </button>
);

const Field = ({ label, cols = "", children }) => (
  <div className={`space-y-2 ${cols}`}>
    <Label className="overline text-white/60">{label}</Label>
    {children}
  </div>
);
